import { useEffect, useState } from "react";
import api, { formatApiError, resolveAssetUrl } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatDate } from "@/lib/statusUtils";
import { toast } from "sonner";
import { Eye, Download, Printer } from "lucide-react";
import BookingPrintView from "@/components/BookingPrintView";

// Format INR with Indian digit grouping (12,34,56,789).
const inr = (n) => (n == null || n === "") ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;

const STATUS_STYLES = {
  pending: { bg: "#EAB30820", color: "#B08B00", label: "Pending Approval" },
  approved: { bg: "#34735520", color: "#347355", label: "Approved" },
  rejected: { bg: "#DC262620", color: "#DC2626", label: "Rejected" },
};

const _DIR_LBL = {
  F: "Full plot", N: "North", S: "South", E: "East", W: "West",
  NE: "North-East", NW: "North-West", SE: "South-East", SW: "South-West",
  P1: "Portion 1", P2: "Portion 2", P3: "Portion 3", P4: "Portion 4",
};

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bookings");
        setBookings(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">My Bookings</div>
          <h1 className="font-heading text-2xl md:text-4xl">Booking Requests</h1>
        </div>

        <div className="border border-[#E5E2DC] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]" data-testid="my-bookings-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Booking #</Th>
                <Th>Plot / Portion</Th>
                <Th>Customer</Th>
                <Th>Price</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Details</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-[#5C6653]">Loading…</td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-[#5C6653]">You have not submitted any bookings yet.</td></tr>
              ) : (
                bookings.map((b) => {
                  const st = STATUS_STYLES[b.status] || STATUS_STYLES.pending;
                  return (
                    <tr key={b.id} className="border-b border-[#E5E2DC]" data-testid={`booking-row-${b.booking_no}`}>
                      <Td><span className="font-mono-anu text-xs">{b.booking_no}</span></Td>
                      <Td>
                        <span className="text-xs text-[#5C6653]">{b.phase_name} · {b.lp_no}</span>
                        <br />
                        <span className="font-medium">Plot #{b.plot_no}</span>
                        {b.is_partial && (
                          <div className="mt-0.5 inline-flex items-center gap-1.5">
                            <span
                              className="text-[10px] px-1.5 py-0.5 border font-medium tracking-wider uppercase"
                              style={{ background: "#F4E6D9", borderColor: "#C06C47", color: "#8B4A2B" }}
                            >
                              {b.portion_code || _DIR_LBL[b.direction] || "Partial"}
                            </span>
                            {b.area_sqyd != null && (
                              <span className="text-[10px] text-[#5C6653] font-mono-anu">{Number(b.area_sqyd).toFixed(0)} sq.yd</span>
                            )}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <div className="font-medium">{b.customer_name}</div>
                        <div className="text-xs text-[#5C6653]">{b.customer_mobile}</div>
                      </Td>
                      <Td>{inr(b.quoted_price)}</Td>
                      <Td>
                        <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: st.bg, borderColor: st.color, color: st.color }}>
                          {st.label}
                        </span>
                        {b.status === "rejected" && b.rejected_reason && (
                          <div className="text-[10px] text-[#5C6653] mt-1 max-w-[220px]">{b.rejected_reason}</div>
                        )}
                      </Td>
                      <Td><span className="text-xs text-[#5C6653]">{formatDate(b.created_at)}</span></Td>
                      <Td>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-sm h-8 text-xs border-[#E5E2DC]"
                          onClick={() => setViewing(b)}
                          data-testid={`view-my-booking-${b.booking_no}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[92vh] overflow-y-auto w-[calc(100vw-1.5rem)] sm:w-full" data-testid="my-booking-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex flex-wrap items-center gap-2">
                  {viewing.booking_no}
                  {viewing.is_partial && (
                    <span
                      className="text-[10px] px-2 py-0.5 border font-medium tracking-wider uppercase"
                      style={{ background: "#F4E6D9", borderColor: "#C06C47", color: "#8B4A2B" }}
                    >
                      Partial Plot
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription className="sr-only">Booking details for {viewing.booking_no}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1 text-sm max-h-[70vh] overflow-y-auto">
                <SectionHeader>Plot</SectionHeader>
                <Row k="Phase / LP" v={`${viewing.phase_name} · ${viewing.lp_no}`} />
                <Row k="Plot" v={`#${viewing.plot_no}`} />
                {viewing.portion_code && <Row k="Portion Code" v={<span className="font-mono-anu text-xs">{viewing.portion_code}</span>} />}
                {viewing.direction && <Row k="Direction" v={_DIR_LBL[viewing.direction] || viewing.direction} />}
                {viewing.area_sqyd != null && <Row k="Area" v={`${Number(viewing.area_sqyd).toFixed(2)} sq.yd`} />}
                {viewing.passbook_no && <Row k="Passbook #" v={viewing.passbook_no} />}

                {viewing.boundaries && Object.values(viewing.boundaries).some((x) => x) && (
                  <>
                    <SectionHeader>Boundaries</SectionHeader>
                    <Row k="North" v={viewing.boundaries.north || "—"} />
                    <Row k="South" v={viewing.boundaries.south || "—"} />
                    <Row k="East" v={viewing.boundaries.east || "—"} />
                    <Row k="West" v={viewing.boundaries.west || "—"} />
                  </>
                )}

                {viewing.sketch_url && (
                  <>
                    <SectionHeader>Survey Sketch</SectionHeader>
                    <SketchPreview url={viewing.sketch_url} />
                  </>
                )}

                <SectionHeader>Customer</SectionHeader>
                <Row k="Name" v={viewing.customer_name} />
                <Row k="Mobile" v={viewing.customer_mobile} />
                <Row k="Email" v={viewing.customer_email || "—"} />
                <Row k="Address" v={viewing.customer_address || "—"} />
                <Row k="ID Reference" v={viewing.id_reference || "—"} />

                <SectionHeader>Payment</SectionHeader>
                <Row k="Quoted Price" v={inr(viewing.quoted_price)} />
                <Row k="Amount Paid" v={inr(viewing.amount_paid)} />
                <Row k="Payment Mode" v={viewing.payment_mode || "—"} />
                <Row k="Payment Option" v={(viewing.payment_option || "full").toUpperCase()} />

                <SectionHeader>Meta</SectionHeader>
                <Row k="Notes" v={viewing.notes || "—"} />
                <Row k="Status" v={viewing.status} />
                <Row k="Created" v={formatDate(viewing.created_at)} />
                {viewing.rejected_reason && <Row k="Rejection Reason" v={viewing.rejected_reason} />}
              </div>
              {viewing.status === "approved" && (
                <div className="pt-3 border-t border-[#E5E2DC] flex justify-end">
                  <button
                    type="button"
                    onClick={() => window.print()}
                    data-testid="print-my-booking-btn"
                    className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white text-sm h-9 px-4 inline-flex items-center gap-2"
                  >
                    <Printer className="h-4 w-4" /> Print
                  </button>
                </div>
              )}
              {viewing.status === "approved" && <BookingPrintView booking={viewing} />}
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1 border-b border-[#E5E2DC] last:border-0">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold col-span-1 self-center">{k}</div>
      <div className="col-span-2 text-sm">{v}</div>
    </div>
  );
}
function SectionHeader({ children }) {
  return (
    <div className="mt-3 mb-1 text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold border-b border-[#C06C47]/20 pb-1">
      {children}
    </div>
  );
}
function SketchPreview({ url }) {
  const absolute = resolveAssetUrl(url);
  const isPdf = /\.pdf($|\?)/i.test(url);
  return (
    <div className="py-2" data-testid="my-booking-sketch-preview">
      {isPdf ? (
        <a href={absolute} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#234B36] underline underline-offset-4 hover:text-[#1A3A29]" data-testid="my-booking-sketch-link">
          <Download className="h-4 w-4" /> Open sketch PDF
        </a>
      ) : (
        <a href={absolute} target="_blank" rel="noreferrer" className="block" data-testid="my-booking-sketch-link">
          <img
            src={absolute}
            alt="Survey sketch"
            className="max-h-64 max-w-full border border-[#E5E2DC] rounded-sm hover:opacity-90 transition-opacity"
          />
          <div className="text-[10px] text-[#5C6653] mt-1">Click image to open full-size</div>
        </a>
      )}
    </div>
  );
}
